import { useState, useRef } from 'react';
import { Text, Grid, ButtonWithDropdown } from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import EditOutline from '@simplywallst/ui-core/icons/EditOutline';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import { Wrapper, Body, Footer, StyledAccessibleButton } from './styled';
import { format } from 'date-fns';
import { useTracking } from 'react-tracking';
import { type NoteFragmentType } from '@/components/Note/hooks/useCompanyNotes';
import { useCompanyNoteModal } from '../CompanyNoteModalContext';
import If from '@/components/If';
import loadable from '@loadable/component';
import { NoteReminderIcon } from '@components/NoteReminder';
import { useTranslation } from '@/hooks/useTranslation';

const DeleteCompanyNoteModal = loadable(
  () => import('../DeleteCompanyNoteModal')
);

interface Props {
  note: NoteFragmentType;
  editNote: (note: NoteFragmentType) => void;
}

export const Note = ({ note, editNote }: Props) => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'note',
    noteId: note.id,
  });
  const { closeNoteModal } = useCompanyNoteModal();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const t = useTranslation();

  const options = useRef([
    {
      label: t('app.component.notes.edit'),
      value: 'EDIT',
      icon: <EditOutline />,
    },
    {
      label: t('app.component.notes.delete'),
      value: 'DELETE',
      icon: <BinSolid />,
    },
  ]).current;

  const handleSelect = (value: string) => {
    trackEvent({
      action: 'select',
      modifier: value,
    });
    switch (value) {
      case 'DELETE':
        handleDeleteNote();
        break;
      case 'EDIT':
        handleEditNote();
        break;
    }
  };

  const handleClose = () => {
    setIsDeleting(false);
    closeNoteModal();
  };
  const handleEditNote = () => {
    trackEvent({
      action: 'click',
    });
    editNote(note);
  };

  const handleDeleteNote = () => {
    trackEvent({
      action: 'click',
    });
    setIsDeleting(true);
  };

  return (
    <Track>
      <Wrapper
        as="section"
        templateRows="106px 35px"
        templateRowsMd="90px 35px"
        templateRowsLg="72px 35px"
        data-cy-id="note"
      >
        <Body onClick={handleEditNote}>
          <StyledAccessibleButton
            type="button"
            aria-label="Edit Note"
            onClick={handleEditNote}
          ></StyledAccessibleButton>
          {note.contentPreview}
        </Body>
        <Footer
          as="footer"
          templateColumns="auto auto"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
            {note.createdAt === note.updatedAt
              ? t('app.component.notes.created')
              : t('app.component.notes.updated')}
            {` `}
            <time dateTime={note.updatedAt}>
              {format(new Date(note.updatedAt), 'dd MMM yyyy')}
            </time>
          </Text>
          <Grid templateColumns="min-content 1fr" alignItems="center">
            {note.reminders && note.reminders.length > 0 && (
              <NoteReminderIcon reminder={note.reminders[0]} />
            )}
            <ButtonWithDropdown
              name="more"
              styleType="passive"
              data-cy-id="note-more"
              options={options}
              onSelect={handleSelect}
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'more',
                });
              }}
            >
              <MoreHorizontalSolid />
            </ButtonWithDropdown>
          </Grid>
        </Footer>
        <If match={isDeleting}>
          {() => <DeleteCompanyNoteModal note={note} onClose={handleClose} />}
        </If>
      </Wrapper>
    </Track>
  );
};
